import * as moment from 'moment';

import { createReducer, combineReducersWithRoot } from '@common/redux-utils';
import { SavedCreditCard } from '@models/Account';
import { PaymentTerms, PaymentType } from '@models/Payment';

import {
  PaymentActionType,
  SetPaymentAmountInitialAction,
  SetPaymentDateAction,
  SetPaymentTermsAction,
  SetPaymentTypeAction,
  SetSavedCreditCardAction,
  SetPaymentSwoAction,
  GetSavedCreditCardsForSwoFailureAction,
  GetSavedCreditCardsForSwoStartAction,
  GetSavedCreditCardsForSwoSuccessAction
} from './payment.actions';
import Constants from '@constants/Constants';
import { ResetPaymentStateAction } from './payment.actions';
import {
  HostedTransactionState,
  initialHostedTransactionState,
  TransactionDataReducer
} from 'src/transaction/transaction.reducer';
import { PaymentMethods } from '@models/HostedCheckoutTransactionViewModel';
import { ContractOrderType } from '@models/Contract';
import { ServiceWorkOrderViewModel } from '@models/ServiceWorkOrder';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface PaymentState {
  readonly installmentsFee: number;
  readonly isPaymentDateInvalid: boolean;
  readonly maxPaymentAmountInitial?: number;
  readonly paymentAmountBalance: number;
  readonly paymentAmountInitial?: number;
  readonly paymentDate: Date;
  readonly paymentPerTermString: string;
  readonly paymentTerms: PaymentTerms;
  readonly paymentTypeBalance: PaymentType;
  readonly paymentTypeInitial: PaymentType;
  readonly remainingBalance: number;
  readonly remainingBalanceDueString: string;
  readonly savedCreditCardBalance?: SavedCreditCard;
  readonly savedCreditCardInitial?: SavedCreditCard;
  readonly totalBalanceDueString: string;
  readonly paymentMethod: PaymentMethods;
  readonly hostedTransaction: HostedTransactionState;
  readonly paymentSwo?: ServiceWorkOrderViewModel;
  readonly savedCreditCards: SavedCreditCard[];
}

// This is the initial state of the page
const initialState: PaymentState = {
  installmentsFee: 0,
  isPaymentDateInvalid: false,
  paymentAmountBalance: 0,
  paymentDate: moment().toDate(),
  paymentPerTermString: '—',
  paymentTerms: PaymentTerms.OneInstallment,
  paymentTypeBalance: PaymentType.CreditCard,
  paymentTypeInitial: PaymentType.CreditCard,
  remainingBalance: 0,
  remainingBalanceDueString: '—',
  totalBalanceDueString: '—',
  paymentMethod: PaymentMethods.CreditCard,
  hostedTransaction: initialHostedTransactionState,
  savedCreditCards: []
};

function handleSetPaymentAmountInitial(state: PaymentState, action: SetPaymentAmountInitialAction): PaymentState {
  const paymentAmount: number = action.paymentAmount || action.totalPrice;
  const paymentAmounts: PaymentAmounts = calculatePaymentAmountsByTerms(
    action.totalPrice,
    paymentAmount,
    state.paymentTerms,
    state.paymentTypeBalance,
    action.orderType
  );
  const calculationStrings = generateCalculationStrings(
    paymentAmount,
    state.paymentTerms,
    state.paymentTypeBalance,
    action.totalPrice,
    action.orderType
  );
  return {
    ...state,
    maxPaymentAmountInitial: paymentAmounts.maxPaymentAmountInitial,
    paymentAmountBalance: paymentAmounts.paymentAmountBalance,
    paymentAmountInitial: action.paymentAmount || undefined,
    paymentPerTermString: calculationStrings.paymentPerTerm,
    remainingBalance: Math.max(action.totalPrice + state.installmentsFee - paymentAmount, 0),
    remainingBalanceDueString: calculationStrings.remainingBalanceDue,
    totalBalanceDueString: calculationStrings.totalBalanceDue
  };
}

function handleSetPaymentDate(state: PaymentState, action: SetPaymentDateAction): PaymentState {
  const newDate: moment.Moment = moment(action.paymentDate);
  const now: moment.Moment = moment();
  let isDateInvalid: boolean = false;

  if (newDate.isBefore(now, 'day')) {
    isDateInvalid = true;
  }
  if (action.contractEndDate) {
    // allowed to renew 90 days after contract end date
    const expirationDate: moment.Moment = moment(action.contractEndDate).add(90, 'days');
    isDateInvalid = newDate.startOf('day').isAfter(expirationDate.startOf('day'), 'day');
  }
  return {
    ...state,
    isPaymentDateInvalid: isDateInvalid,
    paymentDate: action.paymentDate
  };
}

function handleSetPaymentTerms(state: PaymentState, action: SetPaymentTermsAction): PaymentState {
  const paymentTypeBalance: PaymentType =
    action.paymentTerms !== PaymentTerms.TwoInstallments && state.paymentTypeBalance === PaymentType.DemandLetter
      ? PaymentType.CreditCard
      : state.paymentTypeBalance;

  const paymentAmounts: PaymentAmounts = calculatePaymentAmountsByTerms(
    action.totalPrice,
    state.paymentAmountInitial!,
    action.paymentTerms,
    paymentTypeBalance,
    action.orderType
  );
  const calculationStrings = generateCalculationStrings(
    paymentAmounts.paymentAmountInitial,
    action.paymentTerms,
    paymentTypeBalance,
    action.totalPrice,
    action.orderType
  );
  return {
    ...state,
    installmentsFee: paymentAmounts.installmentsFee,
    maxPaymentAmountInitial: paymentAmounts.maxPaymentAmountInitial,
    paymentAmountBalance: paymentAmounts.paymentAmountBalance,
    paymentAmountInitial: paymentAmounts.paymentAmountInitial,
    paymentPerTermString: calculationStrings.paymentPerTerm,
    paymentTerms: action.paymentTerms,
    paymentTypeBalance: paymentTypeBalance,
    paymentTypeInitial:
      action.paymentTerms !== PaymentTerms.OneInstallment && state.paymentTypeInitial === PaymentType.DemandLetter
        ? PaymentType.CreditCard
        : state.paymentTypeInitial,
    remainingBalance: paymentAmounts.remainingBalance,
    remainingBalanceDueString: calculationStrings.remainingBalanceDue,
    totalBalanceDueString: calculationStrings.totalBalanceDue
  };
}

function handleSetPaymentType(state: PaymentState, action: SetPaymentTypeAction): PaymentState {
  const paymentTypeInitial: PaymentType = action.isBalance ? state.paymentTypeInitial : action.paymentType;
  const paymentTypeBalance: PaymentType = action.isBalance ? action.paymentType : state.paymentTypeBalance;
  const paymentTerms: PaymentTerms =
    paymentTypeInitial === PaymentType.DemandLetter
      ? PaymentTerms.OneInstallment
      : paymentTypeBalance === PaymentType.DemandLetter
        ? PaymentTerms.TwoInstallments
        : state.paymentTerms;
  const paymentAmounts: PaymentAmounts = calculatePaymentAmountsByTerms(
    action.totalPrice,
    state.paymentAmountInitial!,
    paymentTerms,
    paymentTypeBalance,
    action.orderType
  );
  const calculationStrings: CalculationStrings = generateCalculationStrings(
    paymentAmounts.paymentAmountInitial,
    paymentTerms,
    paymentTypeBalance,
    action.totalPrice,
    action.orderType
  );

  return {
    ...state,
    installmentsFee: paymentAmounts.installmentsFee,
    maxPaymentAmountInitial: paymentAmounts.maxPaymentAmountInitial,
    paymentAmountBalance: paymentAmounts.paymentAmountBalance,
    paymentAmountInitial: paymentAmounts.paymentAmountInitial,
    paymentPerTermString: calculationStrings.paymentPerTerm,
    paymentTerms: paymentTerms,
    paymentTypeBalance: paymentTypeBalance,
    paymentTypeInitial: paymentTypeInitial,
    remainingBalance: paymentAmounts.remainingBalance,
    remainingBalanceDueString: calculationStrings.remainingBalanceDue,
    totalBalanceDueString: calculationStrings.totalBalanceDue
  };
}

function handleSetSavedCreditCard(state: PaymentState, action: SetSavedCreditCardAction): PaymentState {
  return {
    ...state,
    savedCreditCardBalance: action.isBalance ? action.savedCreditCard : state.savedCreditCardBalance,
    savedCreditCardInitial: action.isBalance ? state.savedCreditCardInitial : action.savedCreditCard
  };
}

function handleResetPaymentState(state: PaymentState, action: ResetPaymentStateAction): PaymentState {
  return {
    ...initialState
  };
}

function handleSetPaymentSwo(state: PaymentState, action: SetPaymentSwoAction) {
  return {
    ...state,
    paymentSwo: action.swoVM
  };
}

/**** GetSavedCreditCardsForSwo ****/
function handleGetSavedCreditCardsForSwoStart(
  state: PaymentState,
  action: GetSavedCreditCardsForSwoStartAction
): PaymentState {
  return state;
}
function handleGetSavedCreditCardsForSwoSuccess(
  state: PaymentState,
  action: GetSavedCreditCardsForSwoSuccessAction
): PaymentState {
  return {
    ...state,
    savedCreditCards: action.savedCreditCards
  };
}
function handleGetSavedCreditCardsForSwoFailure(
  state: PaymentState,
  action: GetSavedCreditCardsForSwoFailureAction
): PaymentState {
  return state;
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const reducer = createReducer(initialState, {
  [PaymentActionType.SetPaymentAmountInitial]: handleSetPaymentAmountInitial,
  [PaymentActionType.SetPaymentDate]: handleSetPaymentDate,
  [PaymentActionType.SetPaymentTerms]: handleSetPaymentTerms,
  [PaymentActionType.SetPaymentType]: handleSetPaymentType,
  [PaymentActionType.SetSavedCreditCard]: handleSetSavedCreditCard,
  [PaymentActionType.ResetPaymentState]: handleResetPaymentState,
  [PaymentActionType.SetPaymentSwo]: handleSetPaymentSwo,
  /**** GetSavedCreditCardsForSwo ****/
  [PaymentActionType.GetSavedCreditCardsForSwoFailure]: handleGetSavedCreditCardsForSwoFailure,
  [PaymentActionType.GetSavedCreditCardsForSwoStart]: handleGetSavedCreditCardsForSwoStart,
  [PaymentActionType.GetSavedCreditCardsForSwoSuccess]: handleGetSavedCreditCardsForSwoSuccess
});

export const PaymentReducer = combineReducersWithRoot(reducer, {
  hostedTransaction: TransactionDataReducer
});

function calculateInstallmentsFee(
  paymentTerms: PaymentTerms,
  paymentTypeBalance: PaymentType,
  orderType?: ContractOrderType
): number {
  return (orderType === ContractOrderType.DTC && paymentTerms === PaymentTerms.OneInstallment) ||
    paymentTypeBalance === PaymentType.DemandLetter ||
    orderType === ContractOrderType.RealtyEscrow ||
    orderType === undefined
    ? 0
    : (paymentTerms as number) * Constants.Payments.INSTALLMENT_SERVICE_FEE;
}

// reusable business-logic
interface PaymentAmounts {
  readonly installmentsFee: number;
  readonly maxPaymentAmountInitial?: number;
  readonly paymentAmountBalance: number;
  readonly paymentAmountInitial: number;
  readonly remainingBalance: number;
}

function calculatePaymentAmountsByTerms(
  totalPrice: number,
  currentPaymentAmountInitial: number,
  paymentTerms: PaymentTerms,
  paymentTypeBalance: PaymentType,
  orderType?: ContractOrderType
): PaymentAmounts {
  const paymentCount: number = paymentTerms as number;
  const totalDeduction: number = 0.01 * (paymentCount - 1);
  const installmentsFee: number = calculateInstallmentsFee(paymentTerms, paymentTypeBalance, orderType);
  let paymentAmountInitial: number = totalPrice;
  if (paymentTerms !== PaymentTerms.OneInstallment) {
    paymentAmountInitial = Math.max(
      0,
      Math.min(currentPaymentAmountInitial, totalPrice - totalDeduction + installmentsFee)
    );
    paymentAmountInitial = Math.floor(paymentAmountInitial * 100) / 100;
  }

  let paymentAmountBalance =
    paymentTerms === PaymentTerms.OneInstallment
      ? 0
      : Math.max(totalPrice + installmentsFee - paymentAmountInitial, 0) / (paymentCount - 1);
  paymentAmountBalance = Math.ceil(paymentAmountBalance * 100) / 100;

  const remainingBalance: number = Math.max(totalPrice + installmentsFee - paymentAmountInitial, 0);

  const maxPaymentAmountInitial: number | undefined =
    paymentTerms !== PaymentTerms.OneInstallment
      ? Math.max(totalPrice + installmentsFee - 0.01 * (paymentTerms - 1), 0.01)
      : undefined;

  return {
    installmentsFee: installmentsFee,
    maxPaymentAmountInitial: maxPaymentAmountInitial,
    paymentAmountBalance: paymentAmountBalance,
    paymentAmountInitial: paymentAmountInitial,
    remainingBalance: remainingBalance
  };
}

interface CalculationStrings {
  readonly paymentPerTerm: string;
  readonly remainingBalanceDue: string;
  readonly totalBalanceDue: string;
}

function generateCalculationStrings(
  paymentAmountInitial: number,
  paymentTerms: PaymentTerms,
  paymentTypeBalance: PaymentType,
  totalPrice: number,
  orderType?: ContractOrderType
): CalculationStrings {
  const installmentsFee: number = calculateInstallmentsFee(paymentTerms, paymentTypeBalance, orderType);

  let paymentPerTerm: string = '—';
  if (paymentTerms !== PaymentTerms.OneInstallment) {
    paymentPerTerm = `$${Math.max(totalPrice + installmentsFee - paymentAmountInitial, 0).toFixed(
      2
    )} / ${(paymentTerms as number) - 1}`;
  }

  let remainingBalanceDue: string = '$'.concat(totalPrice.toFixed(2));
  let totalBalanceDue: string = 'No installment fees';

  if (installmentsFee > 0) {
    remainingBalanceDue = remainingBalanceDue.concat(
      ` + ($${Constants.Payments.INSTALLMENT_SERVICE_FEE} x ${paymentTerms as number})`
    );
    totalBalanceDue = '$'.concat(
      totalPrice.toFixed(2),
      ` + ($${Constants.Payments.INSTALLMENT_SERVICE_FEE} x ${paymentTerms as number})`
    );
  }
  return {
    paymentPerTerm: paymentPerTerm,
    remainingBalanceDue: remainingBalanceDue.concat(' - $', paymentAmountInitial.toFixed(2)),
    totalBalanceDue: totalBalanceDue
  };
}



// WEBPACK FOOTER //
// ./src/payment/payment.reducer.ts